// ClientsTable.js
import React, { useState, useEffect } from "react";
import {
  collection,
  query,
  onSnapshot,
  doc,
  updateDoc,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase";

import StatusPill from "./ui/StatusPill";

const ClientsTable = ({ clientsData = [] }) => {
  /* -------------- FILTROS BÁSICOS (sin cambios) -------------- */
  const excludedEmails = [
    "ornellamelody26@gmail.com",
    "santigimenez427@gmail.com",
    "matiasfernandezvina@hotmail.com",
  ];
  const filteredData = clientsData.filter((u) => {
    const email = u.email || "";
    if (email.endsWith("@mktmarketingdigital.com")) return false;
    if (excludedEmails.includes(email)) return false;
    return true;
  });

  /* -------------- NUEVOS ESTADOS DE FILTRO/ORDEN -------------- */
  const [sortOption, setSortOption] = useState("none"); // none | createdAsc | createdDesc | expAsc | expDesc
  const [clientTypeFilter, setClientTypeFilter] = useState("all"); // all | partner | cliente final
  const [expirationFilter, setExpirationFilter] = useState("all"); // all | expired | valid

  /* estado extra */
  const [whatsFilter, setWhatsFilter] = useState("all"); // all | connected | disconnected
  const [searchTerm, setSearchTerm] = useState("");
  const [threadCounts, setThreadCounts] = useState({});
  const [loadingCounts, setLoadingCounts] = useState({});
  const [loadingLast, setLoadingLast] = useState({});
  // mapea assistantId → Date (última conversación real)
  const [lastConversationMap, setLastConversationMap] = useState({});
  const [expirationDateMap, setExpirationDateMap] = useState({});

  /* -------------- ESTADOS EXISTENTES -------------- */
  const [commentDataMap, setCommentDataMap] = useState({});
  const [assistantsData, setAssistantsData] = useState([]);
  const [clientTypeMap, setClientTypeMap] = useState({});
  const [implementationDateMap, setImplementationDateMap] = useState({});

  /* ordenar por conversaciones  |  convAsc / convDesc / none */
  const [convSort, setConvSort] = useState("none");
  /* filtrar según si quedan conversaciones  |  all / empty / has */
  const [convFilter, setConvFilter] = useState("all");

  /* -------------- SUBSCRIPCIÓN assistants -------------- */
  useEffect(() => {
    const q = query(collection(db, "assistants"));
    const unsub = onSnapshot(q, (snap) => {
      setAssistantsData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, []);

  /* -------------- HELPERS SIN CAMBIOS -------------- */
  const getCommentData = (u) =>
    commentDataMap[u.id] || { text: u.coments || "", edit: false };

  const handleSaveComment = async (id) => {
    const current = commentDataMap[id];
    if (!current) return;
    await updateDoc(doc(db, "users", id), { coments: current.text });
    setCommentDataMap((p) => ({ ...p, [id]: { ...current, edit: false } }));
  };

  const handleClientTypeChange = async (id, newType) => {
    await updateDoc(doc(db, "users", id), { clientType: newType });
    setClientTypeMap((p) => ({ ...p, [id]: newType }));
  };
  const handleImplementationDateChange = async (id, newDate) => {
    await updateDoc(doc(db, "users", id), { implementationDate: newDate });
    setImplementationDateMap((p) => ({ ...p, [id]: newDate }));
  };

  const getQtyClass = (v, danger, warn) => {
    if (v === undefined || v === null) return "bg-gray-100 text-gray-500";
    if (v <= danger) return "bg-red-100 text-red-800 font-semibold";
    if (v <= warn) return "bg-orange-100 text-orange-800 font-semibold";
    return "bg-green-100 text-green-800 font-semibold";
  };

  /* -------------- getExpirationData (modificada) -------------- */
  const getExpirationData = (ts) => {
    if (!ts?.seconds) {
      return {
        label: "--",
        class: "bg-gray-100 text-gray-500",
        expired: false,
      };
    }

    // Diferencia en días entre la fecha de expiración (ts) y ahora
    const diff = (ts.seconds * 1000 - Date.now()) / 86400000;

    // Fecha en formato DD/MM/YYYY (zona de Argentina)
    const dateStr = new Date(ts.seconds * 1000).toLocaleDateString("es-AR", {
      timeZone: "America/Argentina/Buenos_Aires",
    });

    if (diff <= 0) {
      return {
        label: `Expirado (${dateStr})`,
        class: "bg-red-100 text-red-800 font-semibold",
        expired: true,
      };
    }
    if (diff <= 7) {
      return {
        label: `≤ 7 días (${dateStr})`,
        class: "bg-red-100 text-red-800 font-semibold",
        expired: false,
      };
    }
    if (diff <= 14) {
      return {
        label: `≤ 14 días (${dateStr})`,
        class: "bg-orange-100 text-orange-800 font-semibold",
        expired: false,
      };
    }
    // Más de 14 días
    return {
      label: ` (${dateStr})`,
      class: "bg-green-100 text-green-800 font-semibold",
      expired: false,
    };
  };

// --- reemplaza tu handleExpirationDateChange por este ---
const handleExpirationDateChange = async (id, newDate) => {
  // newDate viene del <input type="date"> con formato "YYYY-MM-DD"
  // Construimos "YYYY-MM-DDT23:59:00-03:00"  ➜  23:59  (UTC-3)
  const expiresAtARG = new Date(`${newDate}T23:59:00-03:00`);

  await updateDoc(doc(db, "users", id), {
    accessExpiresAt: Timestamp.fromDate(expiresAtARG),
  });

  // actualizamos el estado local para que el selector muestre el nuevo valor
  setExpirationDateMap(prev => ({ ...prev, [id]: newDate }));
};

  /* -------------- APLICAR FILTROS + ORDEN -------------- */
  const applyFiltersAndSort = () => {
    let arr = [...filteredData];

    // FILTRO POR BÚSQUEDA
    if (searchTerm.trim() !== "") {
      const term = searchTerm.toLowerCase();
      arr = arr.filter(
        (u) =>
          (u.name || "").toLowerCase().includes(term) ||
          (u.email || "").toLowerCase().includes(term)
      );
    }

    // 1. Filtro por tipo de cliente
    if (clientTypeFilter !== "all") {
      arr = arr.filter(
        (u) => (u.clientType ?? "cliente final") === clientTypeFilter
      );
    }

    // 2. Filtro por expiración
    if (expirationFilter !== "all") {
      arr = arr.filter((u) => {
        const exp = getExpirationData(u.accessExpiresAt);
        return expirationFilter === "expired" ? exp.expired : !exp.expired;
      });
    }

    // 3. Filtro por conexión WhatsApp
    if (whatsFilter !== "all") {
      arr = arr.filter((u) => {
        const assist = assistantsData.find((a) => a.ownerId === u.id);
        const connected = assist?.phone_number_id && assist?.waba_id;
        return whatsFilter === "connected" ? connected : !connected;
      });
    }

    // 4. Filtro por conversaciones restantes (histórico)
    if (convFilter !== "all") {
      arr = arr.filter((u) => {
        const usedFree = u.totalThreads || 0;
        const freeRemain = 6000 - usedFree;
        const purchased = u.purchasedThreads || 0;
        const totalRemain = freeRemain + purchased;
        return convFilter === "empty" ? totalRemain <= 0 : totalRemain > 0;
      });
    }

    // 5. ORDEN general
    arr.sort((a, b) => {
      /* orden por fechas */
      if (sortOption === "createdAsc" || sortOption === "createdDesc") {
        const aVal = a.createdAt?.seconds ?? 0;
        const bVal = b.createdAt?.seconds ?? 0;
        return sortOption === "createdAsc" ? aVal - bVal : bVal - aVal;
      }
      if (sortOption === "expAsc" || sortOption === "expDesc") {
        const aVal = a.accessExpiresAt?.seconds ?? 0;
        const bVal = b.accessExpiresAt?.seconds ?? 0;
        return sortOption === "expAsc" ? aVal - bVal : bVal - aVal;
      }
      return 0;
    });

    /* ---------- ORDEN por conversaciones gastadas (gratis) ---------- */
    if (convSort !== "none") {
      arr.sort((a, b) => {
        const usedA = a.totalThreads || 0;
        const usedB = b.totalThreads || 0;
        return convSort === "convAsc" ? usedA - usedB : usedB - usedA;
      });
    }
    // ------------------------------------------------------------------

    return arr;
  };

  const displayData = applyFiltersAndSort();
  const totalUsuarios = displayData.length;

  /* -------------- SUBSCRIPCIÓN threads_{assistantId} PARA ÚLTIMA CONVERSACIÓN -------------- */
  useEffect(() => {
    // Recorremos todos los asistentes y abrimos sus colecciones threads_{assistantId}
    const unsubscribes = assistantsData.flatMap((a) => {
      const colName = `threads_${a.assistantId}`;

      // 1) Contar chats para “Chats en la app”
      setLoadingCounts((l) => ({ ...l, [a.assistantId]: true }));
      const unsubCount = onSnapshot(collection(db, colName), (snap) => {
        setThreadCounts((prev) => ({
          ...prev,
          [a.assistantId]: snap.docs.length,
        }));
        setLoadingCounts((l) => ({ ...l, [a.assistantId]: false }));
      });

      // 2) Cargar todos los threads y calcular la fecha máxima de lastNormalMsgAt
      setLoadingLast((l) => ({ ...l, [a.assistantId]: true }));
      const unsubAll = onSnapshot(collection(db, colName), (snap) => {
        let maxDate = null;

        snap.docs.forEach((docSnap) => {
          const data = docSnap.data();
          const lastMsgIso = data.lastNormalMsgAt; // Viene como cadena ISO
          if (lastMsgIso) {
            const dateObj = new Date(lastMsgIso);
            if (!maxDate || dateObj > maxDate) {
              maxDate = dateObj;
            }
          }
        });

        // Guardamos el Date (o null si no había lastNormalMsgAt)
        setLastConversationMap((prev) => ({
          ...prev,
          [a.assistantId]: maxDate,
        }));
        setLoadingLast((l) => ({ ...l, [a.assistantId]: false }));
      });

      return [unsubCount, unsubAll];
    });

    return () => unsubscribes.forEach((fn) => fn());
  }, [assistantsData]);

  /* -------------- RENDER -------------- */
  return (
    <div className="min-h-screen bg-grayBg p-6">
      <div className="max-w-7xl mx-auto bg-white rounded-xl shadow ring-1 ring-gray-200 p-6">
        <header className="mb-2 flex items-center gap-2">
          <h2 className="text-2xl font-semibold">Lista de usuarios</h2>
          {/* badge con el total */}
          <span className="inline-block bg-primary text-white text-xs font-medium px-2 py-0.5 rounded-full">
            {totalUsuarios}
          </span>
        </header>
        <p className="text-sm text-gray-500 mb-6">
          Alertas de vencimiento, uso, WhatsApp y más.
        </p>

        {/* ----------- CONTROLES DE FILTRO / ORDEN ----------- */}
        <div className="flex flex-wrap gap-4 mb-4">
          {/* BUSCADOR */}
          <input
            type="text"
            placeholder="🔍 Buscar nombre o email"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="border rounded px-2 py-1 text-xs flex-grow min-w-[150px]"
          />

          <select
            value={sortOption}
            onChange={(e) => setSortOption(e.target.value)}
            className="border rounded px-2 py-1 text-xs"
          >
            <option value="none">Orden: --</option>
            <option value="createdAsc">Creación ↑</option>
            <option value="createdDesc">Creación ↓</option>
            <option value="expAsc">Vencimiento ↑</option>
            <option value="expDesc">Vencimiento ↓</option>
          </select>

          <select
            value={clientTypeFilter}
            onChange={(e) => setClientTypeFilter(e.target.value)}
            className="border rounded px-2 py-1 text-xs"
          >
            <option value="all">Todos los tipos</option>
            <option value="cliente final">Cliente final</option>
            <option value="partner">Partner</option>
          </select>

          <select
            value={expirationFilter}
            onChange={(e) => setExpirationFilter(e.target.value)}
            className="border rounded px-2 py-1 text-xs"
          >
            <option value="all">Todos los vencimientos</option>
            <option value="expired">Solo expirados</option>
            <option value="valid">No expirados</option>
          </select>

          <select
            value={whatsFilter}
            onChange={(e) => setWhatsFilter(e.target.value)}
            className="border rounded px-2 py-1 text-xs"
          >
            <option value="all">WhatsApp: todos</option>
            <option value="connected">Conectados</option>
            <option value="disconnected">Desconectados</option>
          </select>

          {/* FILTRO SIN/CON CONVERSACIONES */}
          <select
            value={convFilter}
            onChange={(e) => setConvFilter(e.target.value)}
            className="border rounded px-2 py-1 text-xs"
          >
            <option value="all">Todas las conv.</option>
            <option value="empty">Sin conversaciones</option>
            <option value="has">Con conversaciones</option>
          </select>
          {/* ORDEN POR CONVERSACIONES */}
          <select
            value={convSort}
            onChange={(e) => setConvSort(e.target.value)}
            className="border rounded px-2 py-1 text-xs"
          >
            <option value="none">Orden conv.: --</option>
            <option value="convDesc">Más gastadas (gratis)</option>
            <option value="convAsc">Menos gastadas (gratis)</option>
          </select>
        </div>

        {/* ------------------ TABLA ------------------ */}
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse text-sm">
            {/* ENCABEZADO (sin cambios salvo sticky) */}
            <thead className="bg-gray-50 text-gray-600 uppercase text-xs font-medium tracking-wider">
              <tr>
                {[
                  ["Nombre", "w-36"],
                  ["Email", "w-44"],
                  ["Conv. pagas disponibles", "w-32"],
                  ["Conv. gastadas este mes (gratuitas)", "w-32"],
                  ["Última conversación", "w-32"],
                  ["Creación", "w-28"],
                  ["Vencimiento", "w-32"],
                  ["Tipo", "w-32"],
                  ["Implementación", "w-40"],
                  ["WhatsApp", "w-32"],
                  ["Assistant ID", "w-44"],
                  ["Chats en la app", "w-32"],
                  ["Estado", "w-44"],
                ].map(([title, w], i) => (
                  <th
                    key={title}
                    className={`py-3 px-4 ${w} ${
                      i === 0
                        ? "sticky left-0 z-30 bg-white/90 backdrop-blur"
                        : ""
                    }`}
                  >
                    {title}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-200">
              {displayData.length === 0 && (
                <tr>
                  <td
                    colSpan={13}
                    className="py-8 text-center text-gray-500 italic"
                  >
                    No hay usuarios que coincidan
                  </td>
                </tr>
              )}

              {displayData.map((user, idx) => {
                const createdAt = user.createdAt?.seconds
  ? new Date(user.createdAt.seconds * 1000).toLocaleDateString("es-AR", {
      timeZone: "America/Argentina/Buenos_Aires",
    })
                  : "--";
                const selectedImplDate =
                  implementationDateMap[user.id] ??
                  user.implementationDate ??
                  "";

                // -------------- LÓGICA DE COLORES PARA “Conv. gratis” --------------
                const usedFree = user.totalThreads || 0;
                const GREEN_THRESHOLD = 2500;
                const ORANGE_THRESHOLD = 4500;

                let freeClass = "";
                if (usedFree < GREEN_THRESHOLD) {
                  freeClass = "bg-green-100 text-green-800 font-semibold";
                } else if (usedFree < ORANGE_THRESHOLD) {
                  freeClass = "bg-orange-100 text-orange-800 font-semibold";
                } else {
                  freeClass = "bg-red-100 text-red-800 font-semibold";
                }
                // -----------------------------------------------------------

                const purchased = user.purchasedThreads ?? "--";
                const assist = assistantsData.find(
                  (a) => a.ownerId === user.id
                );
                const whatsOK = assist?.phone_number_id && assist?.waba_id;

                // **nuevo**: buscamos el assistant que tenga al user en allowedUsers
                const userAssist = assistantsData.find((a) =>
                  Array.isArray(a.allowedUsers)
                    ? a.allowedUsers.includes(user.id)
                    : false
                );
                const userAssistId = userAssist?.assistantId ?? "--";

                // -------------- ÓBTENER la Date desde lastConversationMap --------------
                const tsDate = lastConversationMap[userAssistId];
                let lastDate = "--";
                if (tsDate instanceof Date) {
                  lastDate = tsDate.toLocaleDateString("es-AR", {
                    timeZone: "America/Argentina/Buenos_Aires",
                  });
                }
                // ----------------------------------------------------------------------------------------

                // —— BLOQUE PARA “VENCIMIENTO” —— 
                // 1) extraemos el Timestamp de Firestore:
                const expTs = user.accessExpiresAt;
                // 2) lo convertimos a “DD/MM/YYYY” para mostrar en el chip (dentro de getExpirationData)
                // 3) lo convertimos a “YYYY-MM-DD” para que <input type="date"> lo muestre bien
                let expDateForInput = "";
                if (expTs) {
                  const d = new Date(expTs.seconds * 1000); // Date en hora local
                  const yyyy = d.getFullYear();
                  const mm = String(d.getMonth() + 1).padStart(2, "0");
                  const dd = String(d.getDate()).padStart(2, "0");
                  expDateForInput = `${yyyy}-${mm}-${dd}`;
                }
                // 4) pedimos a getExpirationData(...) que devuelva label y clase CSS
                const { label: expLabel, class: expClass } = getExpirationData(
                  expTs
                );
                // —— FIN BLOQUE —— 

                const comment = getCommentData(user);

                return (
                  <tr
                    key={user.id}
                    className={`${
                      idx % 2 ? "bg-white" : "bg-white"
                    } hover:bg-primary/5 transition`}
                  >
                    <td className="py-3 px-4 font-medium sticky left-0 bg-inherit backdrop-blur">
                      {user.name}
                    </td>
                    <td className="py-3 px-4">{user.email}</td>

                    {/* Conv. pagas */}
                    <td className="py-3 px-4 text-center align-middle">
                      <span className="px-2 py-1 rounded bg-gray-100 text-gray-800 font-semibold">
                        {purchased}
                      </span>
                    </td>

                    {/* Conv. gastadas este mes (gratuitas) */}
                    <td className="py-3 px-4 text-center align-middle">
                      <span className={`px-2 py-1 rounded ${freeClass}`}>
                        {usedFree}
                      </span>
                    </td>

                    {/* Última conversación */}
                    <td className="py-3 px-4 text-center align-middle">
                      {loadingLast[userAssistId] ? (
                        <svg
                          className="inline-block h-4 w-4 animate-spin text-gray-500"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v8H4z"
                          />
                        </svg>
                      ) : (
                        <span>{lastDate}</span>
                      )}
                    </td>

                    <td className="py-3 px-4">{createdAt}</td>

                    {/* —— COLUMNA “VENCIMIENTO” COMPLETA —— */}
                    <td className="py-3 px-4">
                      <div className="flex flex-col gap-1">
                        {/* 1) el chip (texto + color) */}
                        <span
                          className={`px-2 py-1 rounded inline-block ${expClass}`}
                        >
                          {expLabel}
                        </span>
                        {/* 2) el date-picker: */}
                        <input
                          type="date"
                          lang="es-AR"
                          value={
                            expirationDateMap[user.id] ?? expDateForInput
                          }
                          onChange={(e) =>
                            handleExpirationDateChange(user.id, e.target.value)
                          }
                          className="border border-black rounded p-1 text-xs focus:outline-none"
                        />
                      </div>
                    </td>

                    <td className="py-3 px-4">
                      <select
                        value={
                          clientTypeMap[user.id] ?? user.clientType ?? "cliente final"
                        }
                        onChange={(e) =>
                          handleClientTypeChange(user.id, e.target.value)
                        }
                        className="border border-gray-300 rounded p-1 text-xs focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        <option value="partner">Partner</option>
                        <option value="cliente final">Cliente Final</option>
                      </select>
                    </td>

                    {/* IMPLEMENTACIÓN */}
                    <td className="py-3 px-4">
                      <div className="flex flex-col gap-1">
                        {/* chip verde / rojo */}
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                            selectedImplDate
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {selectedImplDate ? "Implementado" : "No implementado"}
                        </span>

                        {/* selector de fecha */}
                        <input
                          type="date"
                          lang="es-AR"
                          value={selectedImplDate}
                          onChange={(e) =>
                            handleImplementationDateChange(user.id, e.target.value)
                          }
                          className={`border rounded p-1 text-xs focus:outline-none ${
                            selectedImplDate ? "border-green-600" : "border-red-500"
                          }`}
                        />
                      </div>
                    </td>

                    <td className="py-3 px-4">
                      <StatusPill label={whatsOK ? "Conectado" : "Desconectado"} />
                    </td>

                    {/* Asistente ID */}
                    <td className="py-3 px-4 text-xs font-mono">
                      {userAssistId}
                    </td>

                    {/* Chats en la app */}
                    <td className="py-3 px-4 text-center align-middle">
                      {loadingCounts[userAssistId] ? (
                        <svg
                          className="inline-block h-4 w-4 animate-spin text-gray-500"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v8H4z"
                          />
                        </svg>
                      ) : (
                        <span className="px-2 py-1 rounded bg-purple-100 text-purple-800 font-semibold">
                          {threadCounts[userAssistId] ?? 0}
                        </span>
                      )}
                    </td>

                    {/* Comentarios (sin cambios) */}
                    <td className="py-3 px-4 align-top">
                      {comment.edit ? (
                        <div className="flex flex-col gap-2">
                          <textarea
                            rows={2}
                            value={comment.text}
                            onChange={(e) =>
                              setCommentDataMap((p) => ({
                                ...p,
                                [user.id]: { ...comment, text: e.target.value },
                              }))
                            }
                            className="w-full border border-gray-300 rounded p-1 text-xs focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                          <button
                            className="self-end bg-primary hover:opacity-90 text-white text-xs px-2 py-1 rounded"
                            onClick={() => handleSaveComment(user.id)}
                          >
                            Guardar
                          </button>
                        </div>
                      ) : (
                        <div className="bg-gray-50 rounded p-1 text-xs shadow-inner min-h-[40px]">
                          {comment.text ? (
                            <p className="whitespace-pre-wrap break-words">
                              {comment.text}
                            </p>
                          ) : (
                            <span className="text-gray-400 italic">Sin comentarios</span>
                          )}
                          <div className="mt-1 text-right">
                            <button
                              className="bg-primary/10 hover:bg-primary/20 text-primary text-xs px-2 py-1 rounded"
                              onClick={() =>
                                setCommentDataMap((p) => ({
                                  ...p,
                                  [user.id]: { ...comment, edit: true },
                                }))
                              }
                            >
                              Editar
                            </button>
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ClientsTable;
